// The Arena's map: a dark globe you fall out of onto the store's street,
// every store a pin coloured by how hot it is, the current one pulsing.
// MapLibre with free OpenFreeMap tiles (no key), 3D buildings above zoom
// 14, a heat layer for data mode. Pure DOM, no React: SalesArena owns the
// container and calls these.
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export const STYLE_URL = 'https://tiles.openfreemap.org/styles/dark';
export const HOME = { center: [-121.2908, 37.9577], zoom: 9.6 }; // Stockton
const SRC = 'arena-stores';

export const TEMP_COLOR = { hot: '#ff5a5a', warm: '#ffb13d', cold: '#5b8def', none: '#8b97b8', customer: '#34d399', pilot: '#a78bfa', demo: '#c084fc', lost: '#4b5366' };
export const pinColor = (f) => {
  if (f.stage === 'customer' || f.stage === 'pilot' || f.stage === 'demo') return TEMP_COLOR[f.stage];
  if (f.stage === 'lost') return TEMP_COLOR.lost;
  return TEMP_COLOR[f.temp] || TEMP_COLOR.none;
};

export function createMap(container, { onPick, onMove } = {}) {
  const map = new maplibregl.Map({
    container,
    style: STYLE_URL,
    center: [-98, 38],
    zoom: 1.6,
    pitch: 0,
    attributionControl: { compact: true },
    canvasContextAttributes: { antialias: true },
    maxPitch: 75,
  });
  if (typeof window !== 'undefined') window.__arenaMap = map; // for a console or a harness
  map.addControl(new maplibregl.NavigationControl({ visualizePitch: true, showCompass: true }), 'bottom-right');
  // Anything the rep does with the map (drag, wheel, pinch, double-tap, the
  // +/- and compass buttons, the keyboard) ends the orbit and keeps a fly
  // that is still in the air from starting a new one when it lands.
  const touched = () => { userMoved = true; stopOrbit(); };
  map.on('mousedown', touched); map.on('touchstart', touched); map.on('wheel', touched); map.on('dblclick', touched);
  container.addEventListener('keydown', touched);
  map.once('load', () => { for (const b of container.querySelectorAll('.maplibregl-ctrl-zoom-in, .maplibregl-ctrl-zoom-out, .maplibregl-ctrl-compass')) b.addEventListener('click', touched); });
  // The credits start out unfolded as a faint line of text over the map; fold them to the "i" from the start. A tap on it still opens them.
  const foldCredits = () => { for (const a of container.querySelectorAll('.maplibregl-ctrl-attrib')) a.classList.remove('maplibregl-compact-show'); };
  map.once('load', () => { foldCredits(); setTimeout(foldCredits, 600); });
  map.on('style.load', () => {
    // The globe is the opening shot; a browser that cannot draw it (old GPU, software rendering) stays flat.
    const flat = typeof window !== 'undefined' && (window.__arenaFlat || /[?&]flat=1/.test(window.location.search));
    if (!flat) { try { map.setProjection({ type: 'globe' }); } catch (e) { console.warn('arena: no globe', e); } }
    const style = map.getStyle();
    const vector = Object.keys(style.sources || {}).find((k) => style.sources[k].type === 'vector');
    const firstSymbol = (style.layers || []).find((l) => l.type === 'symbol')?.id;
    if (vector && !map.getLayer('arena-3d')) {
      map.addLayer({
        id: 'arena-3d', type: 'fill-extrusion', source: vector, 'source-layer': 'building', minzoom: 13.5,
        paint: {
          'fill-extrusion-color': ['interpolate', ['linear'], ['coalesce', ['get', 'render_height'], 6], 0, '#1b2238', 40, '#2a3558', 120, '#3b4a7a'],
          'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 13.5, 0, 15, ['coalesce', ['get', 'render_height'], 6]],
          'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
          'fill-extrusion-opacity': 0.9,
        },
      }, firstSymbol);
    }
    if (!map.getSource(SRC)) {
      map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, promoteId: 'id' });
      map.addLayer({
        id: 'arena-heat', type: 'heatmap', source: SRC, maxzoom: 15, layout: { visibility: 'none' },
        paint: {
          'heatmap-weight': ['get', 'weight'],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 14, 2.2],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 6, 10, 12, 34, 15, 60],
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(0,0,0,0)', 0.2, 'rgba(59,130,246,0.35)', 0.45, 'rgba(168,85,247,0.6)', 0.7, 'rgba(249,115,22,0.8)', 1, 'rgba(255,241,118,0.95)'],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.9, 15, 0],
        },
      });
      map.addLayer({
        id: 'arena-halo', type: 'circle', source: SRC, filter: ['==', ['get', 'current'], 1],
        paint: { 'circle-radius': 26, 'circle-color': ['get', 'color'], 'circle-opacity': 0.22, 'circle-blur': 0.6 },
      });
      map.addLayer({
        id: 'arena-pins', type: 'circle', source: SRC,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, ['case', ['==', ['get', 'current'], 1], 7, 3], 12, ['case', ['==', ['get', 'current'], 1], 11, 6], 17, ['case', ['==', ['get', 'current'], 1], 16, 9]],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': ['case', ['==', ['get', 'mine'], 1], '#ffffff', 'rgba(255,255,255,0.45)'],
          'circle-stroke-width': ['case', ['==', ['get', 'current'], 1], 3, ['==', ['get', 'mine'], 1], 1.8, 0.8],
          'circle-opacity': ['case', ['==', ['get', 'stage'], 'lost'], 0.45, 0.95],
        },
      });
      map.addLayer({
        id: 'arena-labels', type: 'symbol', source: SRC, minzoom: 12.5,
        layout: { 'text-field': ['get', 'name'], 'text-size': ['interpolate', ['linear'], ['zoom'], 12.5, 10, 16, 13], 'text-offset': [0, 1.3], 'text-anchor': 'top', 'text-font': ['Noto Sans Regular'], 'text-max-width': 9, 'text-optional': true },
        paint: { 'text-color': '#e8ecf7', 'text-halo-color': 'rgba(6,9,18,0.9)', 'text-halo-width': 1.4 },
      });
      // A pin is a few pixels wide at city zoom; take the nearest one within a fingertip of the tap.
      map.on('click', (e) => { const r = 12; const fs = map.queryRenderedFeatures([[e.point.x - r, e.point.y - r], [e.point.x + r, e.point.y + r]], { layers: ['arena-pins'] }); const f = fs && fs[0]; if (f && onPick) onPick(f.properties.id); });
      map.on('mouseenter', 'arena-pins', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'arena-pins', () => { map.getCanvas().style.cursor = ''; });
    }
  });
  if (onMove) map.on('moveend', () => onMove(map));
  return map;
}

/** Push the stores onto the map. Each item: { id, name, lat, lng, temp, stage, tier, mine, current }. */
export function setStores(map, items) {
  const src = map.getSource(SRC);
  if (!src) return;
  const weight = { A: 1, B: 0.6, C: 0.35 };
  src.setData({
    type: 'FeatureCollection',
    features: items.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng)).map((s) => ({
      type: 'Feature', id: s.id,
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      properties: { id: s.id, name: s.name, stage: s.stage, temp: s.temp || 'none', mine: s.mine ? 1 : 0, current: s.current ? 1 : 0, color: pinColor(s), weight: weight[s.tier] || 0.5 },
    })),
  });
}

export function showHeat(map, on) {
  if (map.getLayer('arena-heat')) map.setLayoutProperty('arena-heat', 'visibility', on ? 'visible' : 'none');
}

// A slow orbit around the current store until the next move or a touch.
// userMoved is set by createMap's listeners the moment the rep touches the
// map, and cleared when we start a fly: a fly the rep interrupted never
// hands the camera back to the orbit.
let orbitFrame = 0;
let userMoved = false;
export function stopOrbit() { if (orbitFrame) cancelAnimationFrame(orbitFrame); orbitFrame = 0; }
function orbit(map) {
  stopOrbit();
  if (userMoved) return;
  let last = performance.now();
  const step = (now) => {
    const dt = now - last; last = now;
    map.setBearing(map.getBearing() + dt * 0.004);
    orbitFrame = requestAnimationFrame(step);
  };
  orbitFrame = requestAnimationFrame(step);
}
const orbitWhenLanded = (map) => { map.once('moveend', () => { if (!userMoved) orbit(map); }); };

/** Fall from wherever we are onto the store's street, then drift around it. */
export function flyToStore(map, p, { orbitAfter = true, padding = undefined } = {}) {
  if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
  stopOrbit(); userMoved = false;
  // padding (a phone, where the sheet covers the bottom) keeps the store in the open part of the screen
  map.flyTo({ center: [p.lng, p.lat], zoom: 17.3, pitch: 64, bearing: Math.random() * 80 - 40, speed: 0.85, curve: 1.55, essential: true, ...(padding ? { padding } : {}) });
  if (orbitAfter) orbitWhenLanded(map);
}

/** Pull back to show every pin (data mode, or the queue is empty). */
export function fitAll(map, items) {
  stopOrbit();
  const pts = items.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  if (!pts.length) { map.flyTo({ ...HOME, pitch: 40, bearing: 0, speed: 0.8, essential: true }); return; }
  const b = new maplibregl.LngLatBounds();
  for (const s of pts) b.extend([s.lng, s.lat]);
  try { map.fitBounds(b, { padding: fitPad(map, { top: 90, bottom: 120, left: 60, right: 480 }), pitch: 45, maxZoom: 12.5, duration: 2600, essential: true }); } catch (e) { console.warn('arena: fitAll', e); }
}
// Padding wider than the map itself (a phone with the desktop insets) makes fitBounds hand maplibre a NaN; fit it to the screen.
function fitPad(map, padding) {
  const c = map.getContainer(); const W = c.clientWidth || 390; const H = c.clientHeight || 700;
  const pad = { ...padding };
  if (pad.left + pad.right > W - 80) { const k = (W - 80) / (pad.left + pad.right); pad.left = Math.floor(pad.left * k); pad.right = Math.floor(pad.right * k); }
  if (pad.top + pad.bottom > H - 80) { const k = (H - 80) / (pad.top + pad.bottom); pad.top = Math.floor(pad.top * k); pad.bottom = Math.floor(pad.bottom * k); }
  return pad;
}

/** The opening shot: the globe, then down to the Valley. */
export function intro(map) {
  stopOrbit();
  map.flyTo({ ...HOME, pitch: 48, bearing: -12, speed: 0.55, curve: 1.7, essential: true });
}

export const streetViewUrl = (p, key) => (key && Number.isFinite(p?.lat) && Number.isFinite(p?.lng))
  ? `https://maps.googleapis.com/maps/api/streetview?size=640x320&location=${p.lat},${p.lng}&fov=80&pitch=0&source=outdoor&key=${encodeURIComponent(key)}`
  : '';
export const earthUrl = (p) => `https://earth.google.com/web/search/${encodeURIComponent([p?.name, p?.address, p?.city, p?.state].filter(Boolean).join(' '))}`;
export const mapsUrl = (p) => p?.place?.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([p?.name, p?.address, p?.city, p?.state].filter(Boolean).join(' '))}`;

// ---------- the city shot and the flags ----------
// Run mode no longer drops onto one roof: it shows the whole city the store
// is in, every store there as a flag with its name, the current one lit.
const sameCity = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const km = (a, b) => { const r = Math.PI / 180; const x = (b.lng - a.lng) * r * Math.cos(((a.lat + b.lat) / 2) * r); const y = (b.lat - a.lat) * r; return Math.sqrt(x * x + y * y) * 6371; };
/** The stores on the board in the current store's city (or within 9 km when the city is blank). */
const MAX_CITY = 80; // flags are DOM markers; a Bay Area city can hold hundreds of restaurants, so keep the nearest
export function cityStores(items, current) {
  if (!current) return [];
  const pts = items.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  const byCity = current.city ? pts.filter((s) => sameCity(s.city, current.city)) : [];
  const list = byCity.length > 1 || !Number.isFinite(current.lat) ? byCity : pts.filter((s) => km(s, current) <= 9);
  if (list.length <= MAX_CITY || !Number.isFinite(current.lat)) return list;
  return list.map((s) => [s.id === current.id ? -1 : km(s, current), s]).sort((a, b) => a[0] - b[0]).slice(0, MAX_CITY).map((x) => x[1]);
}

/** Pull up to the city: every flag in view, the current store near the middle, a slow drift. */
export function flyToCity(map, items, current, { padding = { top: 150, bottom: 150, left: 300, right: 540 } } = {}) {
  if (!current || !Number.isFinite(current.lat)) return;
  stopOrbit(); userMoved = false;
  const pts = cityStores(items, current);
  if (pts.length < 2) { map.flyTo({ center: [current.lng, current.lat], zoom: 14.6, pitch: 58, bearing: Math.random() * 60 - 30, speed: 0.8, curve: 1.5, essential: true }); orbitWhenLanded(map); return; }
  const b = new maplibregl.LngLatBounds();
  for (const s of pts) b.extend([s.lng, s.lat]);
  b.extend([current.lng, current.lat]);
  try { map.fitBounds(b, { padding: fitPad(map, padding), pitch: 56, bearing: Math.random() * 50 - 25, maxZoom: 15.4, duration: 2400, essential: true }); } catch (e) { console.warn('arena: flyToCity', e); return; }
  orbitWhenLanded(map);
}

let flagMarkers = [];
/** HTML flags for the city's stores. items: { id, name, lat, lng, temp, stage, tier, current, mine, score }. */
export function setFlags(map, items, { onPick } = {}) {
  for (const m of flagMarkers) m.remove();
  flagMarkers = [];
  for (const s of items) {
    if (!Number.isFinite(s.lat) || !Number.isFinite(s.lng)) continue;
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `arena-flag${s.current ? ' current' : ''}${s.stage === 'lost' ? ' lost' : ''}`;
    el.style.setProperty('--c', pinColor(s));
    el.title = `${s.name}${s.city ? ` · ${s.city}` : ''}${s.temp ? ` · ${s.temp}` : ''}${s.tier ? ` · tier ${s.tier}` : ''}`;
    el.innerHTML = `<span class="pole"></span><span class="cloth">${s.current ? '<span class="you">NOW</span>' : ''}${escapeHtml(s.name)}${s.score != null ? `<span class="score">${s.score}</span>` : ''}${s.mine ? '<span class="mine">●</span>' : ''}</span>`;
    el.addEventListener('click', (e) => { e.stopPropagation(); if (onPick) onPick(s.id); });
    const m = new maplibregl.Marker({ element: el, anchor: 'bottom-left' }).setLngLat([s.lng, s.lat]).addTo(map);
    flagMarkers.push(m);
  }
}
export function clearFlags() { for (const m of flagMarkers) m.remove(); flagMarkers = []; }
const escapeHtml = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
